import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Mail, Phone, MapPin } from "lucide-react";

const LINKS = {
  Navigation: [
    { label: "Home", to: "/" },
    { label: "About Us", to: "/about" },
    { label: "Products", to: "/products" },
  ],
  Support: [
    { label: "Contact", to: "/contact" },
    { label: "FAQs", to: "/faqs" },
    { label: "Order Tracking", to: "/order-tracking" },
  ],
  Legal: [
    { label: "Shipping Policy", to: "/shipping-policy" },
    { label: "Refund Policy", to: "/refund-policy" },
    { label: "Terms", to: "/terms" },
    { label: "Privacy", to: "/privacy" },
  ],
};

function TiktokIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M16.5 3c.2 1.6 1.1 3.1 2.5 4 .9.6 1.9.9 3 .9v3.1c-1.6 0-3.2-.5-4.5-1.3v6.6a6.5 6.5 0 1 1-6.5-6.5c.4 0 .8 0 1.2.1v3.2a3.6 3.6 0 1 0 2.1 3.2V3h2.2Z" />
    </svg>
  );
}

const SOCIALS = [
  { Icon: Instagram, href: "https://www.instagram.com/tiwani_0", label: "Instagram" },
  { Icon: Facebook, href: "https://www.instagram.com/tiwani_0", label: "Facebook" },
  { Icon: TiktokIcon, href: "https://www.tiktok.com/@tiwani00", label: "TikTok" },
];

const LOGO_SRC = "/assets/logo/tiwani-logo-header.png";

export default function Footer() {
  const scrollPageToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="relative overflow-hidden bg-white text-brand-charcoal border-t border-[#f1e8de]">
      <div
        className="absolute inset-0 opacity-75"
        style={{
          backgroundImage: [
            "radial-gradient(circle at 14% 20%, rgba(196, 30, 42, 0.045), transparent 22%)",
            "radial-gradient(circle at 82% 18%, rgba(245, 240, 232, 0.72), transparent 18%)",
            "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(252,249,245,1) 100%)",
          ].join(", "),
        }}
      />
      <div className="absolute -top-12 right-12 w-48 h-48 rounded-full border border-brand-red/6" />
      <div className="absolute bottom-8 left-[-32px] w-36 h-36 rounded-full bg-brand-cream/45 blur-3xl" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-red/20 to-transparent" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-10">
          <div className="col-span-2 lg:col-span-2">
            <div className="mb-5">
              <Link to="/" onClick={scrollPageToTop} className="inline-flex items-center">
                <img
                  src={LOGO_SRC}
                  alt="Tiwani"
                  className="h-12 sm:h-14 w-auto max-w-[240px] sm:max-w-[320px] object-contain object-left"
                  loading="eager"
                  fetchPriority="high"
                />
              </Link>
            </div>
            <p className="font-body text-sm text-brand-charcoal/65 leading-relaxed max-w-sm mb-6">
              A modern luxury fragrance brand dedicated to crafting elegant, long-lasting perfumes
              that capture style, mood, and essence.
            </p>
            <div className="flex gap-3 mb-7">
              {SOCIALS.map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  className="w-10 h-10 border border-[#eadccd] bg-white/80 rounded-sm flex items-center justify-center text-brand-charcoal/55 hover:border-brand-red hover:text-brand-red hover:bg-brand-red/5 transition-all duration-300"
                >
                  <Icon size={15} className="w-[15px] h-[15px]" />
                </a>
              ))}
            </div>
            <div className="flex flex-col gap-2.5">
              <a
                href="mailto:adebiyiaderinsola6@gmail.com"
                className="flex items-center gap-2 font-body text-sm text-brand-charcoal/65 hover:text-brand-red transition-colors"
              >
                <Mail size={12} /> adebiyiaderinsola6@gmail.com
              </a>
              <a
                href="tel:+2349128129749"
                className="flex items-center gap-2 font-body text-sm text-brand-charcoal/65 hover:text-brand-red transition-colors"
              >
                <Phone size={12} /> +234 912 812 9749
              </a>
              <span className="flex items-center gap-2 font-body text-sm text-brand-charcoal/65">
                <MapPin size={12} /> Ibadan, Oyo State, Nigeria
              </span>
            </div>
          </div>

          {Object.entries(LINKS).map(([title, links]) => (
            <div key={title}>
              <h4 className="font-body text-[10px] uppercase tracking-[0.3em] text-brand-charcoal/35 mb-5">
                {title}
              </h4>
              <ul className="flex flex-col gap-3">
                {links.map(({ label, to }) => (
                  <li key={label}>
                    <Link
                      to={to}
                      onClick={scrollPageToTop}
                      className="font-body text-sm text-brand-charcoal/65 hover:text-brand-red transition-colors duration-200"
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div className="relative border-t border-[#efe5d8] bg-white/70 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="font-body text-xs text-brand-charcoal/45">
            Copyright {new Date().getFullYear()} Tiwani Fragrances. All rights reserved.
          </p>
          <p className="font-body text-xs text-brand-charcoal/35">
            Crafted with care for the love of luxury
          </p>
        </div>
      </div>
    </footer>
  );
}
